import React from 'react';
import { 
    Divider, 
    InfoLabel, InfoLabelProps,
    Link, 
    makeStyles, 
    Skeleton, SkeletonItem, SkeletonProps, Subtitle2, Subtitle2Stronger,
    Text, tokens
} from '@fluentui/react-components';
import { ChatScriptResponse } from '../../Service/ServerAPI';

const useStyles = makeStyles({
    container: {
        marginBottom: '16px',
        transition: '0.3 all ease-in-out',
        BorderColor: 'red 5px',
    

    },
    title: {
        lineHeight: '1.75',
        fontSize: '1rem',
    },
    text: {
        fontWeight: '400',
        fontFamily: 'Segoe UI',
        fontSize: '1rem',
        lineHeight: '2',
        display: 'flex',
        alignItems: 'center',
    },
    userCountLabel: {
        color: '#71BB88',
        display: 'inline',
        "& a, & a:hover": {
            color: '#71BB88',
        },
    },
    executionTime: {
        fontWeight: '400',
        fontFamily: 'Segoe UI',
        fontSize: '1rem',
    },
    users: {
        fontWeight: '400',
        fontFamily: 'Segoe UI',
        fontSize: '1rem',
        lineHeight: '3',
    },
    divider: {
        margin: '12px 0',
    },
    skeletonWrapper: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    skeletonRow: {
        alignItems: 'center',
        display: 'grid',
        paddingBottom: '10px',
        position: 'relative',
        gap: '20px',
        gridTemplateColumns: '80%',
    },
    loadingWrapper: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        width: '100%'
    },
    infoLabel: {
        lineHeight: '0',
        marginLeft: '3px',
    }
});

interface Props {
    result: ChatScriptResponse | null;
    isScriptRunning: boolean;
}

const Results: React.FC<Props> = ({ result, isScriptRunning}) => {
    const classes = useStyles();

    function formatNumber(num: number) {
        return num.toLocaleString();
    }
    
    const marketingUrl = 'https://account.xbox.com/ContactPreferences';
    const personalizedPurchaseUpsellUrl =
        'https://support.xbox.com/en-US/help/family-online-safety/online-safety/personalized-recommendations';


    if (isScriptRunning) {
        return (
            <div className={classes.container} aria-busy> 
                <Skeleton>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className={classes.skeletonRow} style={{ gridTemplateColumns: '70%' }}>
                            <SkeletonItem size={16} />
                        </div>
                        <div className={classes.skeletonRow} style={{ gridTemplateColumns: '40%' }}>
                            <SkeletonItem size={16} />
                        </div>                        
                        <div className={classes.skeletonRow} style={{ gridTemplateColumns: '50%' }}>
                            
                            <SkeletonItem size={16} />
                        </div>
                        
                        <div className={classes.skeletonRow} style={{ gridTemplateColumns: '60%' }}>
                            
                            <SkeletonItem size={16} />
                        </div>
                        <div className={classes.skeletonRow} style={{ gridTemplateColumns: '90%' }}>
                            <SkeletonItem size={16} />
                        </div>
                    </div>
                </Skeleton>    
            </div>
        );
    }

    if (!result) {
        return (
            <div>
                <Text>Something bad happened.</Text>
            </div>
        );
    }

    if (result.script_error) {
        return (
            <div>
                <Text>{result.script_error}</Text>
            </div>
        );
    }

    return (
        <div className={classes.container}> 
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text className={classes.executionTime}>
                    Execution Time: {Math.floor(result.executeTimeMs / 1000)} seconds
                </Text>
                <Text className={classes.users}>
                    Users: {formatNumber(result.users ?? 0)}
                </Text>
            </div>
            <Divider className={classes.divider} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Subtitle2Stronger className={classes.title}>
                    Total User Counts Breakdown
                </Subtitle2Stronger>
                <Text className={classes.text}>
                    <div className={classes.userCountLabel}>
                        Adult Accounts:&nbsp;
                    </div>
                    {formatNumber(result.countAdultAccounts ?? 0)}
                    <InfoLabel className={classes.infoLabel}
                        info={<>Users who are recognized as adults based on their locale's legal age.</>}
                    />
                </Text>
                <Text className={classes.text}>
                    <div className={classes.userCountLabel}>
                        Opted-in to&nbsp;
                        <Link href={marketingUrl} target="_blank" rel="noopener noreferrer">
                            First Party Marketing
                        </Link>:&nbsp;
                    </div>
                    {formatNumber(result.countFirstPartyMarketingOptIn ?? 0)}
                </Text>
                <Text className={classes.text}>
                    <div className={classes.userCountLabel}>
                        Opted-in to&nbsp;
                        <Link href={marketingUrl} target="_blank" rel="noopener noreferrer">
                            Third Party Marketing
                        </Link>
                        :&nbsp;
                    </div>
                    {formatNumber(result.countThirdPartyMarketingOptIn ?? 0)}
                </Text>
                <Text className={classes.text}>
                    <div className={classes.userCountLabel}>
                        Opted-in to&nbsp;
                        <Link href={personalizedPurchaseUpsellUrl} target="_blank" rel="noopener noreferrer">
                            Personalized Purchase Upsell
                        </Link>
                        :&nbsp;
                    </div>
                    {formatNumber(result.countPersonalizedPurchaseUpsellOptIn ?? 0)}
                </Text>
            </div>
        </div>
    );
};

export default Results;
